import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { collection, getDocs, addDoc, deleteDoc, doc, query, where } from 'firebase/firestore';
import { db } from '../firebase';

interface Student {
  id: string;
  name: string;
  grade: string;
  class: string;
  lastActive: string;
  progress: number;
  email?: string;
  parentId?: string;
}

const Students: React.FC = () => {
  const { t } = useTranslation();
  const { userData } = useAuth();
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClass, setSelectedClass] = useState('All');
  const [selectedGrade, setSelectedGrade] = useState('All');

  // Form state for adding a new student
  const [isAddingStudent, setIsAddingStudent] = useState(false);
  const [newStudent, setNewStudent] = useState({
    name: '',
    email: '',
    grade: '1',
    class: 'A',
  });
  
  // Fetch students from Firestore
  useEffect(() => {
    const fetchStudents = async () => {
      try {
        setLoading(true);
        const studentsRef = collection(db, 'students');
        const snapshot = await getDocs(studentsRef);
        
        const fetchedStudents: Student[] = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          // Set default values for any missing fields
          name: doc.data().name || 'Unnamed Student',
          grade: doc.data().grade || '1',
          class: doc.data().class || 'A',
          lastActive: doc.data().lastActive || 'Never',
          progress: doc.data().progress || 0
        } as Student));
        
        setStudents(fetchedStudents);
      } catch (err) {
        console.error('Error fetching students:', err);
        setError(t('students.failedToLoad'));
      } finally {
        setLoading(false);
      }
    };

    fetchStudents();
  }, [t]);

  // Add new student to Firestore
  const handleAddStudent = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newStudent.name || !newStudent.grade || !newStudent.class) {
      setError(t('validation.required'));
      return;
    }
    
    try {
      setLoading(true);
      
      // Add the student to Firestore
      const studentData = {
        ...newStudent,
        lastActive: t('students.justRegistered'),
        progress: 0,
        createdAt: new Date().toISOString(),
        createdBy: userData?.uid
      };
      
      const docRef = await addDoc(collection(db, 'students'), studentData);
      
      // Add the new student to the local state
      setStudents(prevStudents => [
        ...prevStudents, 
        { 
          id: docRef.id, 
          ...studentData,
          lastActive: t('students.justRegistered'),
          progress: 0
        } as Student
      ]);
      
      // Reset the form
      setNewStudent({
        name: '',
        email: '',
        grade: '1',
        class: 'A',
      });
      
      setIsAddingStudent(false);
    } catch (err) {
      console.error('Error adding student:', err);
      setError(t('students.failedToAdd'));
    } finally {
      setLoading(false);
    }
  };

  // Delete a student from Firestore
  const handleDeleteStudent = async (id: string) => {
    if (!window.confirm(t('students.confirmDelete'))) {
      return;
    }
    
    try {
      setLoading(true);
      
      // Delete the student from Firestore
      await deleteDoc(doc(db, 'students', id));
      
      // Remove the student from the local state
      setStudents(prevStudents => prevStudents.filter(student => student.id !== id));
    } catch (err) {
      console.error('Error deleting student:', err);
      setError(t('students.failedToDelete'));
    } finally {
      setLoading(false);
    }
  };
  
  // Filter students based on search and filters
  const filteredStudents = students.filter(student => {
    // Filter by search term
    const matchesSearch = student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (student.email && student.email.toLowerCase().includes(searchTerm.toLowerCase()));
    
    // Filter by class
    const matchesClass = selectedClass === 'All' || student.class === selectedClass;
    
    // Filter by grade
    const matchesGrade = selectedGrade === 'All' || student.grade === selectedGrade;
    
    return matchesSearch && matchesClass && matchesGrade;
  });
  
  // Get unique classes and grades for filters
  const classes = ['All', ...new Set(students.map(student => student.class))];
  const grades = ['All', ...new Set(students.map(student => student.grade))];
  
  // Display loading state
  if (loading && students.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#309D97]"></div>
      </div>
    );
  }
  
  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">{t('common.students')}</h1>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
          <button 
            className="float-right"
            onClick={() => setError('')}
          >
            &times;
          </button>
        </div>
      )}
      
      {/* Add Student Form */}
      {isAddingStudent && (
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <h2 className="text-lg font-medium mb-4">{t('students.addStudent')}</h2>
          <form onSubmit={handleAddStudent}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('students.name')}*</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#309D97]"
                  value={newStudent.name}
                  onChange={(e) => setNewStudent({...newStudent, name: e.target.value})}
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.email')}</label>
                <input
                  type="email"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#309D97]"
                  value={newStudent.email}
                  onChange={(e) => setNewStudent({...newStudent, email: e.target.value})}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('content.grade')}*</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#309D97]"
                  value={newStudent.grade}
                  onChange={(e) => setNewStudent({...newStudent, grade: e.target.value})}
                  required
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8].map(grade => (
                    <option key={grade} value={grade.toString()}>{t('students.gradeNumber', { number: grade })}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('students.class')}*</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#309D97]"
                  value={newStudent.class}
                  onChange={(e) => setNewStudent({...newStudent, class: e.target.value})}
                  required
                >
                  {['A', 'B', 'C', 'D', 'E'].map(cls => (
                    <option key={cls} value={cls}>{t('students.classLetter', { letter: cls })}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                onClick={() => setIsAddingStudent(false)}
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-[#309D97] text-white rounded-md hover:bg-opacity-90"
                disabled={loading}
              >
                {loading ? t('students.adding') : t('students.addStudent')}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Filters and search */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.search')}</label>
            <input
              type="text"
              placeholder={t('students.searchPlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#309D97]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('content.grade')}</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#309D97]"
              value={selectedGrade}
              onChange={(e) => setSelectedGrade(e.target.value)}
            >
              {grades.map(grade => (
                <option key={grade} value={grade}>
                  {grade === 'All' ? t('students.allGrades') : t('students.gradeNumber', { number: grade })}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('students.class')}</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#309D97]"
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
            >
              {classes.map(cls => (
                <option key={cls} value={cls}>
                  {cls === 'All' ? t('students.allClasses') : t('students.classLetter', { letter: cls })}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-500">
            {t('students.showing', { filtered: filteredStudents.length, total: students.length })}
          </div>
          
          {userData?.role === 'admin' && (
            <button 
              className="bg-[#309D97] text-white px-4 py-2 rounded-md hover:bg-opacity-90 transition"
              onClick={() => setIsAddingStudent(!isAddingStudent)}
            >
              {isAddingStudent ? t('common.cancel') : t('students.addStudent')}
            </button>
          )}
        </div>
      </div>
      
      {/* Students Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('common.students')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('students.gradeClass')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('students.lastActive')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('students.progress')}
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('students.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredStudents.map(student => (
              <tr key={student.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-[#309D97] bg-opacity-20 flex items-center justify-center text-[#309D97] font-medium">
                      {student.name.charAt(0)}
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">{student.name}</div>
                      {student.email && (
                        <div className="text-sm text-gray-500">{student.email}</div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{t('students.gradeNumber', { number: student.grade })}</div>
                  <div className="text-sm text-gray-500">{t('students.classLetter', { letter: student.class })}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{student.lastActive}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-700 mr-2">
                        {student.progress}%
                      </span>
                      <div className="flex-grow h-2 w-32 bg-gray-200 rounded-full">
                        <div 
                          className="h-2 rounded-full" 
                          style={{
                            width: `${student.progress}%`,
                            backgroundColor: student.progress > 70 ? '#10B981' : student.progress > 40 ? '#F59E0B' : '#EF4444'
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button className="text-[#309D97] hover:text-[#1e7d77] mr-3">
                    {t('students.viewDetails')}
                  </button>
                  {userData?.role === 'admin' && (
                    <button 
                      className="text-red-600 hover:text-red-800"
                      onClick={() => handleDeleteStudent(student.id)}
                      disabled={loading}
                    >
                      {t('students.remove')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            
            {filteredStudents.length === 0 && !loading && (
              <tr>
                <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                  {students.length === 0 
                    ? t('students.noStudents') 
                    : t('students.noMatchingStudents')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Students; 